import React, { FC, useEffect, useRef, useState } from 'react'

import CloseIcon from '@mui/icons-material/Close'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Stack,
  Typography,
  Chip,
  useTheme,
  Alert,
  AlertTitle,
  LinearProgress,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import { CustomButton } from '../../components/CustomButton'
import Iconify from '../../components/Iconify/Iconify'
import { MarkdownRenderer } from '../../components/Markdown'
import { showToast } from '../../components/Toasts'
import { useAppDispatch } from '../../store/hooks'
import { useIsMobile } from '../../utils/useIsMobile'

import TradeAiAnalysisReference from './TradeAiAnalysisReference'
import { TradeAiAnalysisService } from './TradeAiAnalysisService'
import { setTradeAiAnalysis } from './TradingLogSlice'
import { TradeAiAnalysis, TradeAiAnalysisRequestParams } from './types'

interface Props {
  tradeId: number | null
  open: boolean
  onClose: () => void
  onAnalysisSaved?: (tradeId: number, analysis: TradeAiAnalysis | null) => void
  loading: boolean
  onSetLoading: (tradeId: number, isLoading: boolean) => void
}

export const TradeAiAnalysisModal: FC<Props> = ({
  tradeId,
  open,
  onClose,
  onAnalysisSaved,
  loading,
  onSetLoading,
}) => {
  const { spacing } = useTheme()

  const isMobile = useIsMobile('md')
  const dispatch = useAppDispatch()
  const { t, i18n } = useTranslation()

  const language = (i18n.resolvedLanguage || i18n.language || 'en').split('-')[0]

  const [data, setData] = useState<TradeAiAnalysis | null>(null)

  const requestIdRef = useRef(0) // 🧠 Used to cancel stale async responses if new modal opened between them

  const onGetAnalysis = async (tradeId: number | null, params?: TradeAiAnalysisRequestParams) => {
    if (!tradeId) {
      return
    }

    const currentRequestId = ++requestIdRef.current // ⬅️ bump and snapshot

    onSetLoading?.(tradeId, true)

    try {
      const res = await TradeAiAnalysisService.analyze(tradeId, {
        ...params,
        language,
      })

      dispatch(setTradeAiAnalysis({ tradeId, analysis: res.data }))
      onAnalysisSaved?.(tradeId, res.data)

      // ✅ Only apply response if it's from the latest request
      if (requestIdRef.current === currentRequestId) {
        setData(res.data)
      }
    } catch (e) {
      showToast.error(e.message)

      if (requestIdRef.current === currentRequestId) {
        setData(null)
      }
    } finally {
      onSetLoading?.(tradeId, false)
    }
  }

  const onRegenerateAnalysis = () => {
    onGetAnalysis(tradeId, { regenerate: true })
  }

  useEffect(() => {
    if (open && tradeId && !loading) {
      setData(null)
      onGetAnalysis(tradeId)
    }
  }, [tradeId, open, language])

  return (
    <Dialog open={open} onClose={onClose} maxWidth={'md'} fullWidth fullScreen={isMobile}>
      <DialogTitle>
        {t('tradeAiAnalysisModal.title')}
        <IconButton onClick={onClose} className="close-button">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: spacing(42), height: '100%', textAlign: 'center', p: 2 }}
          >
            <Iconify
              icon={'mingcute:ai-line'}
              width={80}
              height={80}
              sx={{
                color: 'primary.main',
                animation: 'pulseColor 1.5s ease-in-out infinite',
                '@keyframes pulseColor': {
                  '0%': { opacity: 1 },
                  '50%': { opacity: 0.6 },
                  '100%': { opacity: 1 },
                },
              }}
            />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('tradeAiAnalysisModal.loading.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('tradeAiAnalysisModal.loading.note')}
            </Typography>
            <LinearProgress sx={{ width: '100%', mt: 3 }} />
          </Stack>
        ) : data ? (
          <Stack spacing={2}>
            <Alert severity="warning">
              <AlertTitle>{t('tradeAiAnalysisModal.disclaimer.title')}</AlertTitle>
              {t('tradeAiAnalysisModal.disclaimer.text')}
            </Alert>

            <MarkdownRenderer>{data.summary}</MarkdownRenderer>

            {Boolean(data.keyFactor) && (
              <Typography variant="subtitle2" color="text.secondary">
                {t('tradeAiAnalysisModal.keyFactor')}: {data.keyFactor}
              </Typography>
            )}

            <Stack direction="row" gap={1} flexWrap={'wrap'}>
              {data.tags?.map((tag) => (
                <Chip key={tag} label={tag} size="small" />
              ))}
            </Stack>

            {Boolean(data.recommendation) && (
              <Alert severity="info">
                <AlertTitle>{t('tradeAiAnalysisModal.suggestedNextStep.title')}</AlertTitle>
                {data.recommendation}
              </Alert>
            )}

            <Stack spacing={1}>
              {data.references?.map((reference, i) => (
                <TradeAiAnalysisReference data={reference} key={i} />
              ))}
            </Stack>
          </Stack>
        ) : (
          <Stack
            alignItems="center"
            justifyContent="center"
            sx={{ minHeight: spacing(42), height: '100%', textAlign: 'center', p: 2 }}
          >
            <Iconify
              icon={'mingcute:ai-line'}
              width={80}
              height={80}
              sx={{ color: 'primary.main' }}
            />
            <Typography variant="body1" sx={{ mt: 2 }}>
              {t('tradeAiAnalysisModal.analysisNotAvailable.title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {t('tradeAiAnalysisModal.analysisNotAvailable.text')}
            </Typography>
          </Stack>
        )}
      </DialogContent>
      <DialogActions>
        {!loading && (
          <CustomButton color="secondary" onClick={onRegenerateAnalysis}>
            {t('tradeAiAnalysisModal.action.regenerate')}
          </CustomButton>
        )}
        <CustomButton onClick={onClose}>{t('tradeAiAnalysisModal.action.close')}</CustomButton>
      </DialogActions>
    </Dialog>
  )
}

export default TradeAiAnalysisModal
